import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandMark, EventismCredit } from "@/components/Brand";
import PinDisplay from "@/components/PinDisplay";

const MAX_POLLS = 12;
const INTERVAL = 2500;

export default function CheckoutReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { user, refresh: refreshUser } = useAuth();
  const [state, setState] = useState({ phase: "checking", error: null, booking: null });
  const polls = useRef(0);

  useEffect(() => {
    if (state.phase === "paid") {
      // refresh /auth/me so id_verified flag is current
      refreshUser?.();
    }
  }, [state.phase, refreshUser]);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", error: "Missing session_id", booking: null });
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ phase: "paid", error: null, booking: data.booking });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", error: null, booking: data.booking });
          return;
        }
        polls.current += 1;
        if (polls.current >= MAX_POLLS) {
          setState({ phase: "timeout", error: null, booking: data.booking });
          return;
        }
        setTimeout(tick, INTERVAL);
      } catch (e) {
        polls.current += 1;
        if (polls.current >= MAX_POLLS) {
          setState({
            phase: "error",
            error: e.response?.data?.detail || "Could not verify payment",
            booking: null,
          });
          return;
        }
        setTimeout(tick, INTERVAL);
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-brand-slate flex flex-col">
      <div className="px-6 py-5 flex justify-between items-center border-b border-slate-100 bg-white">
        <BrandMark />
        <Link
          to="/dashboard"
          className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-brand-red"
          data-testid="checkout-dashboard-link"
        >
          Dashboard <i className="fas fa-arrow-right ml-2" />
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-3xl">
          {state.phase === "checking" && (
            <div className="bg-white rounded-[32px] p-10 text-center border border-slate-100 shadow-sm" data-testid="checkout-checking">
              <div className="w-20 h-20 mx-auto rounded-3xl bg-brand-blue text-brand-gold flex items-center justify-center mb-6">
                <i className="fas fa-spinner fa-spin text-3xl" />
              </div>
              <h1 className="text-3xl font-black uppercase tracking-tightest text-brand-blue mb-2">
                Confirming Payment
              </h1>
              <p className="text-slate-500 font-bold text-sm">
                Generating your Smart Pod PIN... hang tight.
              </p>
            </div>
          )}

          {state.phase === "paid" && state.booking && (
            <div className="space-y-6 animate-slide" data-testid="checkout-success">
              <div className="text-center mb-2">
                <span className="bg-brand-red text-white text-[10px] font-black uppercase tracking-[0.3em] px-6 py-2 rounded-full inline-block">
                  Payment Confirmed
                </span>
                <h1 className="text-4xl font-black uppercase tracking-tightest text-brand-blue mt-4">
                  You're <span className="text-brand-red">In</span>.
                </h1>
                <p className="text-slate-500 font-bold text-sm mt-2">
                  Your 6-digit unlock code is below. Save it — we'll also keep it on your dashboard.
                </p>
              </div>
              <PinDisplay
                pin={state.booking.pin}
                podAddress={state.booking.pod_address}
                startsAt={state.booking.pin_starts_at}
                endsAt={state.booking.pin_ends_at}
              />
              {user && !user.id_verified && (
                <div
                  className="bg-brand-blue text-white rounded-[28px] p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center gap-4 shadow-[0_24px_60px_-24px_rgba(11,30,67,0.45)]"
                  data-testid="checkout-id-prompt"
                >
                  <div className="w-14 h-14 rounded-2xl bg-brand-gold text-brand-blue flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-id-card text-2xl" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-gold">
                      One-Time Setup
                    </p>
                    <h3 className="text-xl md:text-2xl font-black uppercase tracking-tightest mt-1">
                      Finish Verifying Your Account
                    </h3>
                    <p className="text-[12px] text-white/80 font-medium mt-1.5 leading-snug">
                      Snap your driver's-license front, back, and a quick selfie. Takes 60
                      seconds and we'll never ask again.
                    </p>
                  </div>
                  <button
                    onClick={() => navigate("/profile?from=checkout")}
                    className="bg-brand-gold text-brand-blue px-6 py-3 rounded-xl font-black uppercase tracking-widest text-xs whitespace-nowrap hover:bg-amber-300 transition-colors"
                    data-testid="checkout-verify-button"
                  >
                    Verify Now <i className="fas fa-arrow-right ml-2" />
                  </button>
                </div>
              )}
              <div className="text-center">
                <button
                  onClick={() => navigate("/dashboard")}
                  className="btn-primary px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-sm"
                  data-testid="checkout-go-dashboard"
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          )}

          {state.phase === "timeout" && (
            <Card icon="fa-clock" title="Still Processing" testId="checkout-timeout"
              body="Stripe is taking a bit longer than usual. Check your dashboard in a minute — your PIN will appear there once payment clears." cta="View Dashboard"
              onCta={() => navigate("/dashboard")} />
          )}
          {state.phase === "expired" && (
            <Card icon="fa-times-circle" title="Session Expired" testId="checkout-expired"
              body="The checkout session expired. Please re-add gear to your pack and try again." cta="Browse Pod"
              onCta={() => navigate("/#inventory")} />
          )}
          {state.phase === "error" && (
            <Card icon="fa-exclamation-triangle" title="Couldn't verify payment" testId="checkout-error"
              body={state.error || "Something went wrong. Contact support: 720-907-4044."} cta="Back to Dashboard"
              onCta={() => navigate("/dashboard")} />
          )}

          <div className="mt-8 text-center">
            <EventismCredit />
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ icon, title, body, cta, onCta, testId }) {
  return (
    <div className="bg-white rounded-[32px] p-10 text-center border border-slate-100 shadow-sm" data-testid={testId}>
      <div className="w-20 h-20 mx-auto rounded-3xl bg-brand-blue text-brand-gold flex items-center justify-center mb-6">
        <i className={`fas ${icon} text-3xl`} />
      </div>
      <h1 className="text-3xl font-black uppercase tracking-tightest text-brand-blue mb-3">{title}</h1>
      <p className="text-slate-500 font-bold text-sm max-w-md mx-auto mb-6">{body}</p>
      <button onClick={onCta} className="btn-primary px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-sm">
        {cta}
      </button>
    </div>
  );
}
